import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { Component } from '@theme/component';

class ProductInventory extends Component {
  #section;

  connectedCallback() {
    super.connectedCallback();
    this.#section = this.closest('.shopify-section, dialog');
    this.#section?.addEventListener(ThemeEvents.variantUpdate, this.updateInventory);
    this.addEventListener('click', this.#toggleDeliveryInfo);
    document.addEventListener('click', this.#closeDeliveryInfo);
    document.addEventListener('keydown', this.#onKeydown);
    queueMicrotask(() => this.#initializeDelivery());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#section?.removeEventListener(ThemeEvents.variantUpdate, this.updateInventory);
    this.removeEventListener('click', this.#toggleDeliveryInfo);
    document.removeEventListener('click', this.#closeDeliveryInfo);
    document.removeEventListener('keydown', this.#onKeydown);
  }

  updatedCallback() {
    super.updatedCallback();
    this.#initializeDelivery();
  }

  #initializeDelivery() {
    const delivery = this.querySelector('.serenett-delivery');
    const dateElement = delivery?.querySelector('.serenett-delivery__date');
    if (!(delivery instanceof HTMLElement) || !dateElement) return;

    const numbers = delivery.dataset.shippingTime?.match(/\d+/g);
    const minDays = Number.parseInt(numbers?.[0] ?? '', 10);
    const maxDays = numbers?.[1] ? Number.parseInt(numbers[1], 10) : minDays + 2;
    if (!Number.isFinite(minDays) || !Number.isFinite(maxDays) || minDays < 0 || maxDays < minDays) {
      delivery.hidden = true;
      dateElement.textContent = '';
      return;
    }

    delivery.hidden = false;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const addBusinessDays = (days) => {
      const result = new Date(today);
      for (let added = 0; added < days; ) {
        result.setDate(result.getDate() + 1);
        if (result.getDay() !== 0 && result.getDay() !== 6) added++;
      }
      return result;
    };
    const minDate = addBusinessDays(minDays);
    const maxDate = addBusinessDays(maxDays);
    const formatter = new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' });
    const startYear = minDate.getFullYear();
    const endYear = maxDate.getFullYear();
    dateElement.textContent = `${formatter.format(minDate)}${startYear === endYear ? '' : `, ${startYear}`}–${formatter.format(maxDate)}, ${endYear}`;
  }

  #toggleDeliveryInfo = (event) => {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('.serenett-delivery-info__button');
    if (!button || !this.contains(button)) return;
    event.preventDefault();
    event.stopPropagation();
    const open = button.getAttribute('aria-expanded') !== 'true';
    this.#setDeliveryInfo(open);
  };

  #setDeliveryInfo(open) {
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    this.classList.toggle('is-delivery-open', open && mobile);
    this.querySelector('.serenett-delivery-info')?.classList.toggle('is-open', open && !mobile);
    this.querySelector('.serenett-delivery-info__button')?.setAttribute('aria-expanded', String(open));
    this.querySelector('.serenett-delivery-mobile-message')?.setAttribute('aria-hidden', String(!(open && mobile)));
  }

  #closeDeliveryInfo = (event) => {
    if (!this.contains(event.target) && !window.matchMedia('(max-width: 749px)').matches) {
      this.#setDeliveryInfo(false);
    }
  };

  #onKeydown = (event) => {
    if (event.key === 'Escape') this.#setDeliveryInfo(false);
  };

  /**
   * Updates the inventory.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updateInventory = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const newInventory = event.detail.data.html.querySelector('product-inventory');

    if (!newInventory) return;

    morph(this, newInventory, { childrenOnly: true });
    this.#setDeliveryInfo(false);
    this.#initializeDelivery();
  };
}

if (!customElements.get('product-inventory')) {
  customElements.define('product-inventory', ProductInventory);
}
